
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Investment } from "@/types/investment";
import { transformDatabaseItemToInvestment } from "@/utils/investmentDataTransform";

export const useInvestmentData = () => {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");

  const fetchInvestments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setDebugInfo("Fetching available investments (sanitized)...");
      
      // Fetch sanitized data via secured RPC
      const { data, error } = await (supabase as any).rpc('get_investment_summaries');

      if (error) {
        setDebugInfo(`Query Error: ${error.message}`);
        throw error;
      }

      setDebugInfo(`Query successful. Found ${data?.length || 0} records.`);
      
      // Log raw data for debugging
      console.log("Raw investment summaries:", data);

      if (!data || data.length === 0) {
        setDebugInfo("Database connected but no records found. The sample data may not have been inserted properly.");
        setError('No investment opportunities found in database');
        return;
      }

      // Transform the data using the new utility function
      const transformedInvestments = data.map(transformDatabaseItemToInvestment);

      console.log("Final transformed investments:", transformedInvestments);
      setInvestments(transformedInvestments);
      setDebugInfo(`Successfully loaded ${transformedInvestments.length} investment opportunities.`);
      setError(null);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch investment opportunities';
      setError(errorMessage);
      setDebugInfo(`Final Error: ${errorMessage}`);
      console.error("Investment data fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, []);

  return {
    investments,
    isLoading,
    error,
    debugInfo,
    refetch: fetchInvestments
  };
};
